import { DICTATION_AUDIO_MAX_BYTES, validateDictationAudio } from '@oncatch/types/dictation-audio';

// Widget ↔ admin API client.
// All calls are credentialed via the short-lived submission token issued by /widget-config.
// Tokens are one-shot server-side (replay protection), so the client MUST refresh them
// after every successful submit and whenever they expire. See Issue #27 + L-070.

export interface WidgetConfigResponse {
  uploadPolicy?: { version: 1; maxFiles: 5 | 10; maxTotalBytes: number };
  backgroundAttachmentsEnabled?: boolean;
  sourceTextUploadsEnabled?: boolean;
  dictationEnabled?: boolean;
  enabled: boolean;
  workspaceId?: string;
  domainId?: string;
  sensitiveDataMode?: boolean;
  /**
   * #212 PR-B — workspace's identification_enabled toggle. When true AND
   * sensitiveDataMode is false, the widget will accept OnCatch.identify()
   * payloads and render the form with the "Reporting as X" line in place
   * of the email/name inputs. When false (default), identify() calls are
   * accepted and queued but the widget UI treats identification as
   * inactive and the server silently strips identified_* fields per PR-A.
   */
  identificationEnabled?: boolean;
  /**
   * #648 (OC-5) — workspace's reporter fix-ship notifications toggle. When
   * true, the form renders an optional "Notify me when this is fixed"
   * checkbox, visible only while the reporter email field is non-empty.
   * When false/absent (default), the checkbox is not rendered at all.
   */
  fixNotificationsEnabled?: boolean;
  token?: string;
  tokenExpiresAt?: string;
  /**
   * #136: storage cap state at bootstrap. 'full' = workspace at-or-over its
   * storage cap → widget hides capture buttons + renders "Attachments
   * unavailable" inline. Text-only submission stays available at every state.
   * The 80-99% warning band is invisible to reporters by design.
   */
  storageState?: 'normal' | 'full';
  /**
   * #217: privacy masking config (GDPR/CCPA data minimization). Workspace
   * arrays ADD to the widget's hardcoded defaults; the widget always masks
   * passwords, emails, [data-oncatch-mask], CC patterns, and SSN patterns
   * regardless of these values. Empty arrays from the server / undefined
   * from a legacy server both mean "use defaults only."
   */
  privacyConfig?: {
    maskSelectors: string[];
    redactPatterns: string[];
  };
  /**
   * #241: workspace display name. Empty string = workspace has no name set
   * (workspaces.name is nullable). Widget falls back to generic "this site"
   * copy in that case so the consent modal still reads coherently.
   */
  workspaceName?: string;
  /**
   * #241: workspace's `attachment_retention_days` setting. Used by the
   * recording consent modal copy to state retention accurately. Default 90.
   */
  attachmentRetentionDays?: number;
  /**
   * #56: optional BCP-47 locale override from the workspace's widget config.
   * Takes precedence over `data-locale` on the script tag and `navigator.language`.
   * `null` / unset means the widget falls back to the script-tag attribute, then
   * the browser preference, then `en`.
   */
  locale?: string | null;
}

export interface FailedAttachmentDescriptor {
  clientId: string;
  filename: string;
  contentType: AttachmentContentType;
  attachmentType?: 'screenshot' | 'video' | 'video_with_camera' | 'audio';
  error: 'local_processing_failed' | 'upload_failed';
}

export interface SubmissionPayload {
  attachmentProcessingMode?: 'background';
  failedAttachments?: FailedAttachmentDescriptor[];
  workspaceId: string;
  title: string;
  summary: string;
  reporterName?: string | null;
  reporterEmail?: string | null;
  /**
   * #648 (OC-5) — reporter ticked "Notify me when this is fixed". Only sent
   * when true (additive — stale servers ignore it). Server forces the stored
   * flag to false when reporterEmail is absent.
   */
  notifyOnFix?: boolean;
  metadataJson?: Record<string, unknown>;
  consoleLogJson?: unknown[];
  networkErrorsJson?: unknown[];
  /**
   * v1-mvp #97: per-attempt idempotency key. Stable across the internal token-refresh retry
   * so server-side dedup (UNIQUE on workspace_id + idempotency_key) collapses the second
   * insert to a no-op. New api.submit() calls (e.g. user clicks Submit again) generate a
   * fresh key, so they're treated as new intent — not deduped.
   */
  idempotencyKey?: string;
  /**
   * #161 (#5a): reservations consumed by this submission. Server-side, /submissions
   * iterates each reservationId and calls reconcileReservation → commit + insert
   * attachment row. Per-attachment failure logs and skips; the submission still lands.
   * Server cap: max 20 entries (Zod). Widget UX cap: 6.
   */
  attachments?: Array<{
    reservationId: string;
    /**
     * #297 — present when the widget needs to override the contentType-
     * derived attachment_type (camera-recording produces a video MIME but
     * needs to land as 'video_with_camera' on the DB). Omitted for plain
     * screenshots + screen recordings; server falls back to the legacy
     * derivation when missing.
     */
    attachmentType?: 'screenshot' | 'video' | 'video_with_camera' | 'audio';
  }>;
  /**
   * #215 — Pin-to-element capture data. Present when the user submitted via
   * pin mode (clicked the "Pin a bug" button and then a specific element on
   * the host page). Server validates the shape with Zod; the JSONB CHECK on
   * submissions.pinned enforces required keys at the DB level too.
   */
  pinned?: import('./pin-controller.ts').PinnedData;
}

/**
 * Allowed content types for /api/v1/uploads/reserve. Server-side Zod enum mirrors
 * this exactly; keeping them in sync is part of the #5a + #5c sequence.
 */
export type AttachmentContentType =
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'video/mp4'
  | 'video/webm'
  | 'audio/mp4'
  | 'audio/webm'
  | 'text/plain'
  | 'application/pdf'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Error codes returned by /api/v1/uploads/reserve. These are the raw uppercase strings
 * the server emits — NOT the lowercase ErrorCode enum in @oncatch/types (that's for
 * the aspirational v3.0 contract that doesn't apply here yet).
 *
 * IP_RATE_LIMIT comes from the API layer (Layer 1). Everything else comes from the
 * SQL function reserve_workspace_upload (Layers 2–5) or post-reserve infra failures.
 */
export type ReserveErrorCode =
  | 'INVALID_JSON'
  | 'INVALID_BODY'
  | 'INVALID_SIZE'
  | 'CONTENT_TYPE_REJECTED'
  | 'IP_RATE_LIMIT'
  | 'WIDGET_MISCONFIGURED'
  | 'INVALID_TOKEN'
  | 'WORKSPACE_NOT_FOUND'
  | 'REPORT_STORAGE_LIMIT'
  | 'STORAGE_FULL'
  | 'FILE_TOO_LARGE'
  | 'RATE_LIMIT_HIT'
  | 'DAILY_QUOTA_HIT'
  | 'INTERNAL_ERROR'
  | 'DOCUMENT_REJECTED';

export interface ReserveUploadInput {
  processingMode?: 'background';
  filename: string;
  sizeBytes: number;
  contentType: AttachmentContentType;
}

export type ReserveUploadResponse =
  | {
      ok: true;
      reservationId: string;
      /** Local-only provenance, never read from the server response. */
      credentialGeneration?: number;
      // #536 — `signedUploadUrl` is GONE. The server no longer generates one:
      // uploads go exclusively through the admin-origin proxy (#480), nothing
      // ever read the value, and handing every reporter's browser a
      // direct-write Storage URL bypassed the proxy's byte ceiling and
      // content-type pinning.
      expiresAt: string;
      finalContentType: AttachmentContentType;
      maxBytes: number;
    }
  | {
      ok: false;
      error: ReserveErrorCode;
      retryAfterSeconds?: number;
      maxFileBytes?: number;
      challenge?: {
        type: 'turnstile';
        siteKey: string;
        action: string;
        cdata: string;
      };
    };

/**
 * #480 — input for uploadViaProxy. The widget no longer PUTs bytes directly to
 * a `*.supabase.co` signed URL (blocked by strict host `connect-src`); instead
 * it PUTs to `${apiBaseUrl}/api/v1/uploads/proxy` (admin.oncatch.app, already
 * allowlisted). The server derives the destination object path from the
 * reservationId — the client never supplies a path/URL.
 */
export interface UploadViaProxyInput {
  credentialGeneration?: number;
  processingMode?: 'background';
  reservationId: string;
  blob: Blob;
  contentType: AttachmentContentType;
}

/**
 * #480 — `status` is the HTTP status (or 0 on a network-level failure);
 * `error` distinguishes the failure class so callers can branch on retry vs.
 * give-up. `aborted` covers both an unbound workspace and the #533 upload
 * timeout below.
 */
export type UploadViaProxyResponse =
  | { ok: true; processing?: boolean }
  | {
      ok: false;
      status: number;
      error: 'http_error' | 'network_error' | 'aborted' | 'credential_changed';
    };

export interface ReleaseUploadInput {
  reservationId: string;
}

export type ReleaseUploadResponse =
  | { ok: true; note?: 'already_released' }
  | {
      ok: false;
      error:
        | 'INVALID_JSON'
        | 'INVALID_BODY'
        | 'INVALID_TOKEN'
        | 'RESERVATION_NOT_FOUND'
        | 'TOKEN_MISMATCH'
        | 'ALREADY_COMMITTED'
        | 'IP_RATE_LIMIT'
        | 'INTERNAL_ERROR';
      retryAfterSeconds?: number;
    };

export interface SubmissionResponse {
  ok: boolean;
  submissionId?: string;
  referenceCode?: string;
  createdAt?: string;
  error?: string;
  /** Populated when server demands a Turnstile challenge. Caller runs it + retries. */
  challenge?: {
    type: 'turnstile';
    siteKey: string;
    action: string;
    cdata: string;
  };
  /**
   * #256 — per-attachment outcome from the server's reconcile loop. One entry
   * per submitted reservation. Empty array (or undefined for stale clients)
   * when no attachments were submitted. Lets the widget surface a partial-
   * failure state when reconcile drops some attachments — the failure mode
   * that produced the silent video-loss bug filed in #256.
   */
  attachmentResults?: Array<{
    reservationId: string;
    status: 'committed' | 'processing' | 'failed';
    reason?: string;
  }>;
}

export interface SubmitOptions {
  /** Local-only generation of the credential that reserved these background files. */
  attachmentCredentialGeneration?: number;
  /** Turnstile token obtained from a challenge. Sent as X-Turnstile-Token. */
  turnstileToken?: string;
}

/**
 * If the token is within this many ms of expiring, we refresh it before sending the
 * next request. Gives us a buffer against clock skew and slow-RTT submissions.
 */
const TOKEN_EXPIRY_BUFFER_MS = 30_000;

/**
 * #533 — hard ceiling on the attachment-byte PUT so a hung upload always
 * terminates. Chosen ABOVE the proxy route's `maxDuration = 175` (see
 * apps/admin/src/app/api/v1/uploads/proxy/route.ts) so the client can never
 * abort an upload the server would still have accepted.
 */
const UPLOAD_TIMEOUT_MS = 190_000;

/**
 * Generate a UUIDv4 for idempotency keys. crypto.randomUUID() requires a secure context
 * (HTTPS or localhost) so it isn't available on HTTP customer origins; fall back to
 * crypto.getRandomValues() then Math.random(). Uniqueness — not cryptographic strength —
 * is what matters for dedup keys.
 */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

export class ApiClient {
  private token: string | null = null;
  private credentialGeneration = 0;
  private tokenExpiresAt: number | null = null; // epoch ms
  private workspaceId: string | null = null;
  private refreshInFlight: Promise<boolean> | null = null;
  /**
   * #263: Global Privacy Control signal received from the browser
   * (`navigator.globalPrivacyControl === true`). Bootstrap reads this on
   * widget init and calls setGpc; the value then rides along on every
   * widget-config / submission / heartbeat request as defense-in-depth so
   * the server can record the signal even when intermediaries strip the
   * native `Sec-GPC: 1` HTTP header. OnCatch does not sell personal
   * information or run targeted ads, so the signal triggers no behavior
   * change at the data layer — but 11 US states require receiving and
   * recording it as compliance evidence.
   */
  private gpc = false;

  constructor(private readonly apiBaseUrl: string) {}

  /** Called once from bootstrap after fetchConfig succeeds. */
  bindWorkspace(workspaceId: string) {
    this.workspaceId = workspaceId;
  }

  getCredentialGeneration(): number {
    return this.credentialGeneration;
  }

  setToken(token: string, expiresAt: string | undefined) {
    if (this.token !== token) this.credentialGeneration++;
    this.token = token;
    this.tokenExpiresAt = expiresAt ? Date.parse(expiresAt) : null;
  }

  /**
   * #774 — the hosted recorder may use the current submission token only in a
   * fragment to claim a user-initiated, top-level capture surface. Never
   * persist or log this value. Refresh before returning so a popup is not
   * navigated with an already-expired credential.
   */
  async getFreshToken(): Promise<string | null> {
    if (!this.isTokenFresh() && !(await this.refreshToken())) return null;
    return this.token;
  }

  /** Paid draft attempts are never retried or stored as attachments. */
  async transcribeDraft(blob: Blob, attemptId: string, signal: AbortSignal): Promise<string> {
    if (
      signal.aborted ||
      !this.workspaceId ||
      blob.type !== 'audio/wav' ||
      blob.size > DICTATION_AUDIO_MAX_BYTES
    )
      throw new Error('dictation_unavailable');
    if (!validateDictationAudio(new Uint8Array(await blob.arrayBuffer())) || signal.aborted)
      throw new Error('dictation_audio_invalid');
    const token = await this.getFreshToken();
    if (!token || signal.aborted) throw new Error('dictation_unavailable');
    // MediaRecorder-capable browsers can lack the newer AbortSignal static helpers.
    const controller = new AbortController();
    const abort = () => controller.abort();
    const timer = setTimeout(abort, 120_000);
    signal.addEventListener('abort', abort, { once: true });
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/dictation?attempt=${encodeURIComponent(attemptId)}`,
        {
          method: 'POST',
          credentials: 'omit',
          redirect: 'error',
          headers: {
            'content-type': blob.type.split(';')[0] ?? '',
            'x-oncatch-token': token,
            'x-oncatch-workspace': this.workspaceId,
          },
          body: blob,
          signal: controller.signal,
        },
      );
      if (!response.ok || !response.body) throw new Error('dictation_unavailable');
      const reader = response.body.getReader(),
        decoder = new TextDecoder();
      let text = '',
        size = 0;
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          size += value.byteLength;
          if (size > 32_000) throw new Error('dictation_response_too_large');
          text += decoder.decode(value, { stream: true });
        }
        text += decoder.decode();
      } finally {
        await reader.cancel();
      }
      const result = JSON.parse(text) as { attemptId?: unknown; transcript?: unknown };
      if (
        result.attemptId !== attemptId ||
        typeof result.transcript !== 'string' ||
        result.transcript.length > 5000
      )
        throw new Error('dictation_invalid_response');
      return result.transcript;
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
    }
  }

  /**
   * #263: surface the browser's Global Privacy Control signal to the server
   * via every subsequent request. Bootstrap reads `navigator.globalPrivacyControl`
   * once on widget init and calls setGpc(true) when present. Idempotent —
   * later calls overwrite earlier values, but the signal isn't expected to
   * change within a single page session.
   */
  setGpc(value: boolean): void {
    this.gpc = value;
  }

  private isTokenFresh(): boolean {
    if (!this.token || !this.tokenExpiresAt) return false;
    return Date.now() + TOKEN_EXPIRY_BUFFER_MS < this.tokenExpiresAt;
  }

  // #492: `attempts` defaults to 1 (single try — preserves the original
  // behavior for callers like refreshToken on the submit hot path). The
  // bootstrap mount path passes a higher count to retry transient failures.
  /** True while an admin Verify check is watching this origin (install-watch route). */
  async probeInstallWatch(workspaceId: string): Promise<boolean> {
    const url = new URL(`${this.apiBaseUrl}/api/v1/install-watch`);
    url.searchParams.set('workspace', workspaceId);
    try {
      const res = await fetch(url.toString(), { method: 'GET', credentials: 'omit' });
      if (!res.ok) return false;
      const body = (await res.json()) as { active?: boolean };
      return Boolean(body.active);
    } catch {
      return false;
    }
  }

  async fetchConfig(workspaceId: string, attempts = 1): Promise<WidgetConfigResponse> {
    const url = new URL(`${this.apiBaseUrl}/api/v1/widget-config`);
    url.searchParams.set('workspace', workspaceId);
    // #263: GPC is a GET request — encode the signal as a query param.
    // Server still also reads the `Sec-GPC: 1` HTTP header (the canonical
    // browser-set channel) so this is defense-in-depth for cases where
    // an intermediary strips Sec-* headers.
    if (this.gpc) {
      url.searchParams.set('gpc', '1');
    }
    // #492: the widget mounts ONLY after this resolves, so a single transient
    // failure (common on flaky mobile/LTE) must not permanently hide the widget.
    // Retry with exponential backoff + a per-attempt timeout. A SUCCESSFUL
    // response (including enabled:false) is returned as-is and never retried —
    // only network errors, timeouts, and 5xx/429 are treated as transient.
    const MAX_ATTEMPTS = Math.max(1, attempts);
    const TIMEOUT_MS = 8000;
    let lastErr: unknown;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        const backoff = Math.min(3000, 400 * 3 ** (attempt - 1)) + Math.floor(Math.random() * 250);
        await new Promise((resolve) => setTimeout(resolve, backoff));
      }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      try {
        const res = await fetch(url.toString(), {
          method: 'GET',
          credentials: 'omit',
          signal: controller.signal,
        });
        if (res.ok) return (await res.json()) as WidgetConfigResponse;
        lastErr = new Error(`widget-config ${res.status}`);
        // Non-2xx: retry transient server/edge failures only; others are terminal.
        if (res.status < 500 && res.status !== 429) break;
      } catch (err) {
        // Network error, abort/timeout, or JSON parse failure — retryable.
        lastErr = err;
      } finally {
        clearTimeout(timer);
      }
    }
    throw lastErr ?? new Error('widget-config fetch failed');
  }

  /**
   * Silently refetch widget-config to get a fresh token. Deduplicates concurrent
   * refreshes (two submit clicks racing each other = one network call).
   * Returns true if a usable token is now in hand.
   */
  async refreshToken(): Promise<boolean> {
    if (!this.workspaceId) return false;
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = (async () => {
      try {
        const config = await this.fetchConfig(this.workspaceId as string);
        if (!config.enabled || !config.token) return false;
        this.setToken(config.token, config.tokenExpiresAt);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();
    return this.refreshInFlight;
  }

  /**
   * Fire-and-forget token pre-warm. Called after a successful submit so the next
   * click already has a valid token ready. Failures are intentionally swallowed;
   * if pre-warm fails, the next real submit will lazily refresh.
   */
  prewarmNextToken(): void {
    this.refreshToken().catch(() => undefined);
  }

  /**
   * #533 — refresh the token when the panel OPENS, not inline in front of the
   * submission POST.
   *
   * Tokens live 15 minutes. On a return visit — or any page left open past the
   * TTL — `submit()`'s freshness check fired a full /widget-config round trip
   * (itself several sequential queries) directly in the reporter's submit path,
   * so the slowest submissions were the ones where the reporter had been
   * reading the page for a while. Moving the refresh to panel-open hides it
   * behind the time the reporter spends typing.
   *
   * Gated on freshness so a panel-open with a valid token costs zero requests
   * and never burns the 10/min token_issued_attempt budget. Deduped by the
   * existing refreshInFlight, so an immediate submit awaits the SAME in-flight
   * refresh rather than firing a second one. submit()'s own freshness check
   * remains the fallback and is unchanged.
   */
  prewarmTokenIfStale(): void {
    if (this.isTokenFresh()) return;
    this.refreshToken().catch(() => undefined);
  }

  /**
   * #161 (#5a): reserve a slot for an attachment upload. Calls /api/v1/uploads/reserve
   * which atomically checks Layers 2/3/4/5 of the storage cap (per-file size, hourly
   * upload-rate, total workspace cap, daily attachment count) and returns a
   * reservation id the widget then uploads against via uploadViaProxy.
   *
   * Refreshes the token if it's expired. Surfaces typed error codes for the UI to
   * branch on (STORAGE_FULL, FILE_TOO_LARGE, RATE_LIMIT_HIT, etc.).
   *
   * Server returns either { ok: true, ... } or { ok: false, error, ... } with HTTP 200 in
   * the rejection case (typed error envelope). HTTP 4xx is reserved for malformed
   * requests; HTTP 5xx for unrecoverable server failures.
   */
  async reserveUpload(
    input: ReserveUploadInput,
    options?: SubmitOptions,
  ): Promise<ReserveUploadResponse> {
    if (!this.workspaceId) {
      return { ok: false, error: 'WIDGET_MISCONFIGURED' };
    }
    if (!this.isTokenFresh()) {
      const refreshed = await this.refreshToken();
      if (!refreshed || !this.token) {
        return { ok: false, error: 'INVALID_TOKEN' };
      }
    }

    const credentialGeneration = this.credentialGeneration;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      'x-oncatch-token': this.token as string,
    };
    if (options?.turnstileToken) {
      headers['x-turnstile-token'] = options.turnstileToken;
    }

    let res: Response;
    try {
      res = await fetch(`${this.apiBaseUrl}/api/v1/uploads/reserve`, {
        method: 'POST',
        credentials: 'omit',
        headers,
        body: JSON.stringify({
          workspaceId: this.workspaceId,
          filename: input.filename,
          sizeBytes: input.sizeBytes,
          contentType: input.contentType,
          ...(input.processingMode ? { processingMode: input.processingMode } : {}),
        }),
      });
    } catch {
      return { ok: false, error: 'INTERNAL_ERROR' };
    }

    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      return { ok: false, error: 'INTERNAL_ERROR' };
    }

    const result = parsed as ReserveUploadResponse;
    return result.ok && input.processingMode === 'background'
      ? { ...result, credentialGeneration }
      : result;
  }

  /**
   * #161 (#5a): explicitly release a reservation. Idempotent on the server side
   * (already-released → ok:true with note='already_released').
   *
   * Called when:
   *   - the user closes the widget panel with pending reservations (cleanup)
   *   - an upload fails irrecoverably
   *   - the user explicitly removes an attachment slot before submit
   *
   * Best-effort: callers should not block UX on the response. The 5-min reservation
   * TTL + release_expired_reservations cron handle the case where this never lands.
   */
  async releaseUpload(input: ReleaseUploadInput): Promise<ReleaseUploadResponse> {
    if (!this.workspaceId) {
      return { ok: false, error: 'INVALID_TOKEN' };
    }
    if (!this.isTokenFresh()) {
      const refreshed = await this.refreshToken();
      if (!refreshed || !this.token) {
        return { ok: false, error: 'INVALID_TOKEN' };
      }
    }

    let res: Response;
    try {
      res = await fetch(`${this.apiBaseUrl}/api/v1/uploads/release`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'content-type': 'application/json',
          'x-oncatch-token': this.token as string,
        },
        body: JSON.stringify({
          workspaceId: this.workspaceId,
          reservationId: input.reservationId,
        }),
      });
    } catch {
      return { ok: false, error: 'INTERNAL_ERROR' };
    }

    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      return { ok: false, error: 'INTERNAL_ERROR' };
    }

    return parsed as ReleaseUploadResponse;
  }

  /**
   * #480 — upload attachment bytes THROUGH the admin origin instead of directly
   * to Supabase Storage. PUTs the raw blob to `${apiBaseUrl}/api/v1/uploads/proxy`
   * with the workspace + reservation identifiers in headers (the body is the raw
   * bytes, so identifiers can't ride in a JSON body). The server authenticates
   * the token, looks up the reserved object path from the reservationId, and
   * writes the bytes to Storage — so the browser only ever talks to
   * admin.oncatch.app and a single `connect-src admin.oncatch.app` entry covers
   * the whole widget under a strict host CSP.
   *
   * Needs the submission token + workspace from client state, so it refreshes
   * the token first if it's expired.
   *
   * The proxy returns its rejections as HTTP 200 with `{ ok:false, error }`
   * (typed envelope) for the cases the widget already maps (FILE_TOO_LARGE,
   * RESERVATION_NOT_FOUND, etc.) and HTTP 4xx for malformed requests. Either way
   * we collapse to `http_error` here so the existing per-slot UI + submit-blocker
   * flow behaves identically to the old direct-PUT path.
   */
  async uploadViaProxy(input: UploadViaProxyInput): Promise<UploadViaProxyResponse> {
    if (!this.workspaceId) {
      return { ok: false, status: 0, error: 'aborted' };
    }
    if (!this.isTokenFresh()) {
      const refreshed = await this.refreshToken();
      if (!refreshed || !this.token) {
        return { ok: false, status: 0, error: 'aborted' };
      }
    }

    if (
      input.processingMode === 'background' &&
      input.credentialGeneration !== this.credentialGeneration
    )
      return { ok: false, status: 0, error: 'credential_changed' };

    // #533 — abort ceiling on the byte upload.
    //
    // This fetch had NO signal, so a hung PUT pended forever. That was survivable
    // when submit didn't wait for uploads; under the settle gate it is not — the
    // reporter would wait indefinitely with Remove as the only exit, a state
    // where the submit flow neither proceeds nor opens the blocker modal.
    //
    // 190s sits deliberately ABOVE the route's `maxDuration = 175`: the server
    // abandons the request at 175s, so this ceiling can never abort an upload the
    // server would still have accepted. It exists purely to guarantee
    // termination. On abort the slot transitions to `failed` through the
    // existing path (error UI, blocker eligibility, upload_pipeline_failure
    // relay) and retry() gets a fresh fetch + timer.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
    try {
      let res: Response;
      try {
        res = await fetch(`${this.apiBaseUrl}/api/v1/uploads/proxy`, {
          method: 'PUT',
          credentials: 'omit',
          headers: {
            'content-type': input.contentType,
            'x-oncatch-token': this.token as string,
            'x-oncatch-workspace': this.workspaceId,
            'x-oncatch-reservation': input.reservationId,
            ...(input.processingMode ? { 'x-oncatch-processing-mode': input.processingMode } : {}),
          },
          body: input.blob,
          signal: controller.signal,
        });
      } catch {
        return controller.signal.aborted
          ? { ok: false, status: 0, error: 'aborted' }
          : { ok: false, status: 0, error: 'network_error' };
      }

      if (!res.ok) {
        return { ok: false, status: res.status, error: 'http_error' };
      }

      // The proxy returns its typed rejections as HTTP 200 `{ ok:false, error }`.
      // Parse the JSON envelope so a rejection isn't mistaken for a successful
      // upload. A 200 with no/garbled body is treated as success (the server only
      // emits ok:true with an empty-ish body on the happy path).
      let parsed: { ok?: boolean; processing?: boolean } | null = null;
      try {
        parsed = (await res.json()) as { ok?: boolean; processing?: boolean };
      } catch {
        // No JSON body on a 200 — treat as success (defensive; server always
        // sends a JSON envelope, but a proxy/CDN could strip it).
        return input.processingMode
          ? { ok: false, status: res.status, error: 'http_error' }
          : { ok: true };
      }
      if (input.processingMode && (parsed?.ok !== true || parsed.processing !== true))
        return { ok: false, status: res.status, error: 'http_error' };
      if (parsed && parsed.ok === false) {
        return { ok: false, status: res.status, error: 'http_error' };
      }
      return { ok: true };
    } finally {
      clearTimeout(timer);
    }
  }

  async heartbeat(workspaceId: string): Promise<void> {
    if (!this.token) return;
    await fetch(`${this.apiBaseUrl}/api/v1/heartbeat`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'x-oncatch-token': this.token,
      },
      // #263: GPC defense-in-depth on the heartbeat channel — every page
      // load fires a heartbeat, so this guarantees compliance evidence
      // even for visitors who never actually submit a report.
      body: JSON.stringify(this.gpc ? { workspaceId, gpc: true } : { workspaceId }),
    }).catch(() => undefined);
  }

  /**
   * #163 PR C — best-effort widget-side audit event ingest. Currently used
   * by the recording flow for `recording_consent_accepted`,
   * `recording_started`, `recording_stopped`. Same auth + Origin contract
   * as /heartbeat; tokens aren't consumed (one token can post several
   * events through the recording lifecycle). Fire-and-forget — failures
   * never surface to the widget UI.
   */
  async submitWidgetEvent(input: {
    workspaceId: string;
    eventType:
      | 'recording_consent_accepted'
      | 'recording_started'
      | 'recording_stopped'
      // #297 — partial-fulfillment marker for the camera-overlay flow.
      | 'camera_denied_fallback';
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    if (!this.token) return;
    await fetch(`${this.apiBaseUrl}/api/v1/widget-events`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'x-oncatch-token': this.token,
      },
      body: JSON.stringify({
        workspaceId: input.workspaceId,
        eventType: input.eventType,
        metadata: input.metadata,
      }),
    }).catch(() => undefined);
  }

  /**
   * #34 — admin-relay error capture. Widget global error / unhandledrejection
   * handlers POST here so the admin app can captureMessage via the
   * server-side Sentry SDK. Fire-and-forget; the relay must NEVER throw
   * back at the caller (a throwing error reporter is a feedback loop).
   * Same auth + Origin contract as /widget-events; tokens not consumed.
   */
  async submitWidgetError(input: {
    workspaceId: string;
    /**
     * `error` and `unhandledrejection` come from the global error handler.
     * `upload_pipeline_failure` (#256) is a known-failure-mode signal fired
     * from AttachmentManager when the proxy upload fails (non-2xx, network
     * error, or the #533 timeout) — server captures at warning level so these
     * don't drown out actual exceptions.
     */
    errorKind: 'error' | 'unhandledrejection' | 'upload_pipeline_failure';
    message: string;
    stack?: string;
    url?: string;
    lineno?: number;
    colno?: number;
    widgetVersion?: string;
    context?: Record<string, string | number | boolean>;
  }): Promise<void> {
    if (!this.token) return;
    await fetch(`${this.apiBaseUrl}/api/v1/widget-error`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'x-oncatch-token': this.token,
      },
      body: JSON.stringify(input),
    }).catch(() => undefined);
  }

  async submit(payload: SubmissionPayload, options?: SubmitOptions): Promise<SubmissionResponse> {
    const hasBoundAttachments =
      payload.attachmentProcessingMode === 'background' && (payload.attachments?.length ?? 0) > 0;
    const expectedGeneration = options?.attachmentCredentialGeneration;
    // Proactive refresh: if we know the token is expired (or near-expired),
    // swap it before the request goes out. Costs one round trip at worst.
    if (!this.isTokenFresh()) {
      const refreshed = await this.refreshToken();
      if (!refreshed || !this.token) {
        return { ok: false, error: 'widget_disabled_or_offline' };
      }
    }

    if (hasBoundAttachments && expectedGeneration !== this.credentialGeneration)
      return { ok: false, error: 'attachment_credentials_changed' };

    // Stable across the internal retry below — same key = same submission server-side.
    const idempotencyKey = payload.idempotencyKey ?? generateIdempotencyKey();
    // #263: GPC compliance evidence on submission. Field omitted when absent
    // so the server-side Zod schema's `.optional()` keeps stale clients valid.
    const bodyPayload = { ...payload, idempotencyKey, ...(this.gpc ? { gpc: true } : {}) };

    const send = async (): Promise<SubmissionResponse> => {
      if (!this.token) return { ok: false, error: 'no_token' };
      if (hasBoundAttachments && expectedGeneration !== this.credentialGeneration)
        return { ok: false, error: 'attachment_credentials_changed' };
      const headers: Record<string, string> = {
        'content-type': 'application/json',
        'x-oncatch-token': this.token,
      };
      if (options?.turnstileToken) {
        headers['x-turnstile-token'] = options.turnstileToken;
      }
      const res = await fetch(`${this.apiBaseUrl}/api/v1/submissions`, {
        method: 'POST',
        credentials: 'omit',
        headers,
        body: JSON.stringify(bodyPayload),
      });
      try {
        return (await res.json()) as SubmissionResponse;
      } catch {
        return { ok: false, error: 'invalid_response' };
      }
    };

    let result = await send();

    // Server demanded a Turnstile challenge — surface to caller. Do NOT retry
    // from here; the widget UI must render the challenge, get a token from the
    // reporter, then call submit() again with turnstileToken set.
    if (result.error === 'challenge_required' && result.challenge) {
      return result;
    }

    // Reactive refresh: if the server says enabled:false (likely token_replayed
    // or origin reject), try ONCE more with a fresh token. Single retry — we
    // don't want to mask real misconfigurations with infinite loops.
    // The server returns { enabled: false } as the failure envelope, which
    // overlaps with SubmissionResponse's optional fields at runtime.
    const rawResult = result as unknown as Record<string, unknown>;

    // Rate limiting is NOT a stale-token problem, so the refresh-and-resend
    // below cannot fix it. Retrying spends a second request against the same
    // hourly cap that just rejected the first, so one reporter click burns two
    // of their ten and exhaustion arrives twice as fast. Return the server's
    // verdict as-is and let the UI explain the wait.
    if (rawResult.error === 'rate_limited') return result;

    const enabledFalse = rawResult.enabled === false || (!result.ok && !result.referenceCode);
    if (enabledFalse && !result.challenge) {
      const refreshed = await this.refreshToken();
      if (refreshed && this.token) {
        if (hasBoundAttachments) return { ok: false, error: 'attachment_credentials_changed' };
        result = await send();
      } else {
        return { ok: false, error: 'widget_disabled_or_offline' };
      }
    }

    return result;
  }
}
